import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SettingsService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const SettingsForm: React.FC = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [settingsData, setSettingsData] = useState<SettingsState | null>(null);

  const schema = yup.object().shape({
    email: yup
      .string()
      .email(t("settings.invalidEmail"))
      .required(t("settings.emailRequired")),
    contact: yup.object().shape({
      phone: yup.string().required(t("settings.phoneRequired")),
      city: yup.string().required(t("settings.cityRequired")),
      zip: yup.string().required(t("settings.zipRequired")),
      address: yup.string().required(t("settings.addressRequired")),
    }),
  });

  interface SettingsState {
    email: string;
    contact: {
      phone: string;
      city: string;
      zip: string;
      address: string;
    };
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SettingsState>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "",
      contact: {
        phone: "",
        city: "",
        zip: "",
        address: "",
      },
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await SettingsService.getSingle();
        const settings = response;

        setSettingsData(settings);
      } catch (error) {
        ErrorToast(error);
      }
    };

    fetchSettings();
  }, [id]);

  useEffect(() => {
    if (settingsData) {
      reset(settingsData);
    }
  }, [settingsData, reset]);

  const onSubmit = async (data: SettingsState) => {
    try {
      await SettingsService.update(data);
      SuccessToast(t("settings.successUpdate"));
      navigate("/dashboard");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("settings.email")}</label>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="email"
                            className={`form-control ${
                              errors.email ? "is-invalid" : ""
                            }`}
                            placeholder={t("settings.email")}
                            {...field}
                          />
                        )}
                      />
                      {errors.email && (
                        <div className="invalid-feedback">
                          {errors.email.message}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("settings.phone")}</label>
                      <Controller
                        name="contact.phone"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${
                              errors.contact?.phone ? "is-invalid" : ""
                            }`}
                            placeholder={t("settings.phone")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.phone && (
                        <div className="invalid-feedback">
                          {errors.contact.phone.message}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("settings.city")}</label>
                      <Controller
                        name="contact.city"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${
                              errors.contact?.city ? "is-invalid" : ""
                            }`}
                            placeholder={t("settings.city")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.city && (
                        <div className="invalid-feedback">
                          {errors.contact.city.message}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("settings.zip")}</label>
                      <Controller
                        name="contact.zip"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${
                              errors.contact?.zip ? "is-invalid" : ""
                            }`}
                            placeholder={t("settings.zip")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.zip && (
                        <div className="invalid-feedback">
                          {errors.contact.zip.message}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("settings.address")}
                      </label>
                      <Controller
                        name="contact.address"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${
                              errors.contact?.address ? "is-invalid" : ""
                            }`}
                            placeholder={t("settings.address")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.address && (
                        <div className="invalid-feedback">
                          {errors.contact.address.message}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t("settings.updateSettings")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default SettingsForm;